import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Done as DoneIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

// メールアドレスを一部隠して表示するコンポーネント
interface MaskedEmailDisplayProps {
  email: string | null;
  isBold?: boolean; // 太字表示するかどうかのオプション
}

const MaskedEmailDisplay: React.FC<MaskedEmailDisplayProps> = ({ email, isBold = true }) => {
  const [ showFullEmail, setShowFullEmail ] = useState(false);
  const [ copied, setCopied ] = useState(false);
  const toggleEmailVisibility = (event: React.MouseEvent) => {
    // イベントの伝播を停止して親要素のクリックイベントが発火しないようにする
    event.stopPropagation();
    setShowFullEmail(!showFullEmail);
  };
  const handleCopyEmail = async(event: React.MouseEvent) => {
    // イベントの伝播を停止
    event.stopPropagation();
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      // 2秒後に元に戻す
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };
  // メールアドレスの一部をマスクする関数
  const maskEmail = (email: string | null): string => {
    if (!email) return '[メールアドレスなし]';
    const parts = email.split('@');
    if (parts.length !== 2) return email;
    const [ username, domain ] = parts;
    if (!username || !domain) return email;
    const maskedUsername = username.length <= 3
      ? username[0] + '***'
      : username.substring(0, 3) + '***';
    const domainParts = domain.split('.');
    const tld = domainParts.pop(); // 最後の部分（.com, .jpなど）
    const domainName = domainParts.join('.');
    return `${maskedUsername}@${domainName.substring(0, 1)}***${tld ? '.' + tld : ''}`;
  };
  // メールアドレスがない場合は表示のみ
  if (!email) {
    return (
      <Typography component="span" sx={{
        fontWeight: isBold ? 'bold' : 'normal',
        fontStyle: 'italic',
        color: 'text.secondary'
      }}>
        [メールアドレスなし]
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }} className="no-propagation">
      <Typography component="span" sx={{ fontWeight: isBold ? 'bold' : 'normal' }}>
        {showFullEmail ? email : maskEmail(email)}
      </Typography>
      <Tooltip title={showFullEmail ? "メールアドレスを隠す" : "メールアドレスを表示する"}>
        <IconButton
          size="small"
          onClick={toggleEmailVisibility}
          sx={{ ml: 1 }}
          className="no-propagation"
        >
          {showFullEmail ? <VisibilityOffIcon fontSize="small"/> : <VisibilityIcon fontSize="small"/>}
        </IconButton>
      </Tooltip>
      <Tooltip title={copied ? "コピーしました" : "メールアドレスをコピー"}>
        <IconButton
          size="small"
          onClick={handleCopyEmail}
          sx={{ ml: 0.5 }}
          className="no-propagation"
        >
          {copied ? (
            <DoneIcon fontSize="small" sx={{ color: 'success.main' }}/>
          ) : (
            <ContentCopyIcon fontSize="small"/>
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};
export default MaskedEmailDisplay;
